/*
 * Copyright (C) 2020 Reciprocity, Inc - All Rights Reserved
 * Unauthorized use, copying, distribution, displaying, or public performance
 * of this file, via any medium, is strictly prohibited. All information
 * contained herein is proprietary and confidential and may not be shared
 * with any third party without the express written consent of Reciprocity, Inc.
 */

import gql from 'graphql-tag';
import { mapFilters, applyFormattingRules, sortSeriesByDateLabel } from '@/utils/DashboardUtils';
import { DIMENSIONS, DIMENSION_TYPES, DimensionOptions } from '@/constants/DashboardConstants';
import { MAPPED_COLORS_FORMATTING_RULES } from '@/constants/DashboardFormattingRulesConstants';

// TODO: Change backend API to use the Node id instead of dbId
export const SERIES_BLOCK_QUERY = gql`
  query DashboardBlockSeries(
    $metric: DashboardMetric!
    $dimension: DashboardDimension
    $mappedObject: DashboardMetric
    $mappedObjectAttr: DashboardDimension
    $customAttrId: String
    $customAttrType: String
    $filters: [DashboardFilter!]
  ) {
    dashboardSeries(
      metric: $metric
      dimension: $dimension
      mappedObject: $mappedObject
      mappedObjectAttr: $mappedObjectAttr
      customAttrId: $customAttrId
      customAttrType: $customAttrType
      filters: $filters
    ) {
      data {
        value
        label
        labelId: id
      }
    }
  }
`;

export function seriesVariables(blockSettings) {
  const { metric, dimension, mappedObject, mappedObjectAttr, customAttrId, customAttrType, filters } = blockSettings;

  const params = {
    metric,
    dimension: dimension ? dimension : null,
    mappedObject: mappedObject ? mappedObject : null,
    mappedObjectAttr: mappedObjectAttr ? mappedObjectAttr : null,
    customAttrId: customAttrId ? customAttrId : null,
    customAttrType: customAttrType ? customAttrType : null,
    ...{ filters: filters ? mapFilters(filters) : [] },
  };

  return params;
}

export function seriesUpdate(result, blockSettings) {
  if (!result.dashboardSeries) return;

  const { data } = result.dashboardSeries;
  if (!blockSettings.dimension) {
    return data.sort((a, b) => a.value - b.value);
  }

  if (blockSettings.dimension) {
    const dimensionOptions = DimensionOptions[blockSettings.dimension];
    return dimensionOptions && dimensionOptions.type === DIMENSION_TYPES.DATE
      ? data.sort(sortSeriesByDateLabel)
      : data.sort((a, b) => a.value - b.value);
  }
}

export function seriesFormattedAndFiltered(blockSettings, series) {
  let rules = [];
  if (blockSettings.formatting) {
    rules = rules.concat(blockSettings.formatting);
  }

  // coloring charts mapped colors
  if (
    [
      DIMENSIONS.DESIGN,
      DIMENSIONS.OPERATIONALLY,
      DIMENSIONS.CONCLUSION,
      DIMENSIONS.STATUS,
      DIMENSIONS.RISK_RATING,
      DIMENSIONS.RATING,
    ].indexOf(blockSettings.dimension) !== -1
  ) {
    rules = rules.concat(MAPPED_COLORS_FORMATTING_RULES);
  }

  if (series) {
    const formattedSeries = series;
    // removal of html tags in string
    formattedSeries.forEach(item => {
      item.label = item.label.replace(/(<([^>]+)>)/gi, ' ');
    });

    return applyFormattingRules(rules, formattedSeries) || [];
  }

  return [];
}

export function isObjectAttributeValueSelected(filters) {
  const filteredItems = filters.filter(f => !f.object);
  const filteredItemsLength = filteredItems.length;

  return filteredItemsLength ? filteredItems.filter(f => f.values.length).length === filteredItemsLength : true;
}

export function isMappedObjectAttributeValueSelected(filters) {
  const filteredItems = filters.filter(f => f.object);
  const filteredItemsLength = filteredItems.length;

  return filteredItemsLength ? filteredItems.filter(f => f.values.length).length === filteredItemsLength : true;
}
